import { useState } from "react";
import { useParams } from "react-router-dom";

import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Segmented } from "../../components/Segmented";
import { WaterLimitSheet } from "../../components/WaterSheets";
import { todayStr } from "../../domain/date";
import { dinnerSettings } from "../../domain/dinnerNudge";
import { pronouns } from "../../domain/family";
import { foodOn } from "../../domain/intake";
import type { KayaState, WaterSettings } from "../../domain/types";
import { isOlder, litres, remindersSummary, waterSettings, waterTarget } from "../../domain/water";
import { useLinked, type Linked } from "../../lib/linked";
import type { LinkedPart } from "../../sync/linked";
import { useKaya } from "../../store/kayaStore";
import { setDinnerReminder, setWaterSettings } from "../../store/toggles";
import { DinnerTimeSheet } from "../settings/AlertSettings";
import { LinkedShell, familyPath } from "./linkedPage";

const DIURETIC_OPTIONS: { value: NonNullable<WaterSettings["diuretic"]>; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];

/**
 * A linked person's water settings and Log dinner? reminder (ADR-046). Each change is written at
 * once through the link with the same writers their phone uses, so it carries a stamp and merges
 * on their next sync like a change from a second phone of theirs. Only what the link may write is
 * here: the reminder times at 60+ are the fixed five, and bottles and sip size stay with the
 * person who pours.
 */
export default function FamilyReminders() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  return (
    <LinkedShell
      q={q}
      title={(name) => (name ? `${name}’s reminders` : "Reminders")}
      back={{ to: familyPath(id), label: "Back" }}
    >
      {(l) => <Reminders l={l} write={q.write} />}
    </LinkedShell>
  );
}

function Reminders({
  l,
  write,
}: {
  l: Linked;
  write: (parts: readonly LinkedPart[], fn: (d: KayaState) => void) => Promise<LinkedPart[]>;
}) {
  const showToast = useKaya((s) => s.showToast);
  const [open, setOpen] = useState<"limit" | "dinner" | null>(null);
  const { S, name, devices } = l;
  const p = pronouns(S);
  const ds = todayStr();
  const set = waterSettings(S);
  const t = waterTarget(S, ds);
  const dinner = dinnerSettings(S);

  const send = (parts: readonly LinkedPart[], fn: (d: KayaState) => void) =>
    write(parts, fn).then(
      (overruled) => {
        if (overruled.length) showToast(`${name} changed this on ${p.their} phone since — ${p.their} setting was kept`);
      },
      (e: unknown) => showToast(e instanceof Error ? e.message : "Could not save — try again")
    );
  const water = (patch: Partial<Omit<WaterSettings, "at">>) => void send(["water"], (d) => setWaterSettings(d, patch));

  if (!foodOn(S))
    return <Notice tone="info">{name} has food logging switched off, so there are no food or water reminders.</Notice>;

  return (
    <>
      <Notice tone={devices ? "info" : "caution"} icon={I.phone}>
        {devices
          ? `Reminders go from Kaya’s server to ${name}’s phone. A change here reaches it with ${p.their} next sync.`
          : `${name}’s phone has not switched Kaya’s notifications on yet, so nothing set here arrives until ${p.their} phone does, under You › Water reminders.`}
      </Notice>

      <RowGroup heading="Water">
        <NavRow
          kind="toggle"
          icon={I.glass}
          title="Water reminders"
          subtitle={remindersSummary(S, ds)}
          checked={set.remindOn}
          onChange={(v) => water({ remindOn: v })}
        />
        <NavRow
          icon={I.info}
          title="A doctor's fluid limit"
          subtitle={set.limitMl ? `${litres(set.limitMl)} L a day, shown as a limit` : "None"}
          onClick={() => setOpen("limit")}
        />
      </RowGroup>

      {isOlder(S, ds) && !t?.ceiling ? (
        <section className="k-group" aria-labelledby="fam-diuretic">
          <h2 className="k-group__h" id="fam-diuretic">
            Is one of {p.their} BP tablets a water tablet (diuretic)?
          </h2>
          <div className="you-stack">
            <Segmented<string>
              ariaLabel={`Is one of ${p.their} BP tablets a water tablet?`}
              value={set.diuretic ?? null}
              options={DIURETIC_OPTIONS}
              onChange={(v) => water({ diuretic: v as WaterSettings["diuretic"] })}
            />
            {t?.caution ? <Notice tone="caution">{t.caution}</Notice> : null}
          </div>
        </section>
      ) : null}

      <RowGroup heading="Food">
        <NavRow
          kind="toggle"
          icon={I.bowl}
          title="Log dinner?"
          subtitle="A nudge if no dinner is logged by then"
          checked={dinner.on}
          onChange={(v) => void send(["dinner"], (d) => setDinnerReminder(d, { on: v }))}
        />
        {dinner.on ? <NavRow title="Asks at" value={dinner.at} onClick={() => setOpen("dinner")} /> : null}
      </RowGroup>

      {open === "limit" ? (
        <WaterLimitSheet
          limitMl={set.limitMl}
          onClose={() => setOpen(null)}
          onSave={(ml) => {
            water({ limitMl: ml });
            setOpen(null);
          }}
        />
      ) : null}
      {open === "dinner" ? (
        <DinnerTimeSheet
          at={dinner.at}
          onClose={() => setOpen(null)}
          onDone={(at) => {
            void send(["dinner"], (d) => setDinnerReminder(d, { at }));
            setOpen(null);
          }}
        />
      ) : null}
    </>
  );
}
